import {
    NON_VISIT_TASK_ONLY_SERVICE_IDS,
    OASIS_DEATH_BASE_SERVICE_ID,
    OASIS_DEATH_OT_SERVICE_ID,
    OASIS_DEATH_PT_SERVICE_ID,
    OASIS_DEATH_ST_SERVICE_ID,
    queuesForVariation,
} from '@axle-health/work-queue-definitions';
import { Ordering, VisitFilter } from 'gql-types';

import { ContainerBulkAction } from '../ContainerVisitTable.types';
import {
    defaultEpisodeOfCareRowToLink,
    defaultEpisodeOrdersRowToLink,
    defaultEpisodeVisitsRowToLink,
    defaultVisitsRowToLink,
} from '../definitions';
import { definitionFor, fromDefinition } from '../fromDefinition';
import {
    EpisodeOfCareDashboardContainer,
    OrderDashboardContainer,
    PatientDashboardContainer,
    VisitDashboardContainer,
} from '../types';
import { PINNACLE_CONTAINER_KEYS } from './containerKeys/containerKeys';
import { IDashboardVariationConfig } from './types';

const PUBLISHED_QUEUES = queuesForVariation('PINNACLE_HEALTH_UAT');

const patientSchedulingLink = ({ id }: { id: string }): string => `/patients/${id}/scheduling`;
const allEpisodesSchedulingLink = ({ id }: { id: string }): string =>
    `/patients/${id}/scheduling?episode=all`;

const CANCEL_VISITS = { bulkActions: [ContainerBulkAction.CANCEL_VISITS] };

// OASIS Death assessments (E1 + E2). Real scheduled visits, not tasks.
const OASIS_DEATH_SERVICE_IDS = [
    // E1 (RN/OT/PT/ST)
    '92482',
    '92493',
    '92488',
    '92499',
    // E2 (RN/OT/PT/ST)
    OASIS_DEATH_BASE_SERVICE_ID,
    OASIS_DEATH_OT_SERVICE_ID,
    OASIS_DEATH_PT_SERVICE_ID,
    OASIS_DEATH_ST_SERVICE_ID,
];

// OASIS Transfer assessments (E1 + E2). Real scheduled visits, not tasks.
const OASIS_TRANSFER_SERVICE_IDS = [
    // E1 (RN/OT/PT/ST)
    '92479',
    '92490',
    '92485',
    '92496',
    // E2 (RN/OT/PT/ST)
    '99560',
    '99571',
    '99566',
    '99577',
];

const NON_VISIT_TASK_SERVICE_IDS = [
    ...NON_VISIT_TASK_ONLY_SERVICE_IDS,
    ...OASIS_DEATH_SERVICE_IDS,
    ...OASIS_TRANSFER_SERVICE_IDS,
];

// Scopes the Today's Visits table to real visits, excluding paperwork tasks and the
// Misc discipline. Not a queue, so it stays here rather than in the package.
const TODAYS_VISITS_EXCLUDE_NON_VISIT_TASKS_FILTER: VisitFilter = {
    NOT: {
        services: { externalIds: { value: { inList: NON_VISIT_TASK_SERVICE_IDS } } },
        OR: {
            disciplines: { code: { exact: 'Misc' } },
        },
    },
};

const PINNACLE_INCOMPLETE_CARE_TEAM_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.incompleteCareTeam),
    {
        defaultEnabled: false,
        hoverText: 'Patients that need care teams assigned.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients with incomplete care teams.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const SOC_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.soc),
    {
        defaultEnabled: true,
        hoverText: 'SOCs that need to be scheduled.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No SOC visits to schedule.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const ROC_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.roc),
    {
        defaultEnabled: true,
        hoverText: 'ROCs that need to be scheduled.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No ROC visits to schedule.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const EVAL_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.eval),
    {
        defaultEnabled: true,
        hoverText: 'Evals that need to be scheduled.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No Eval visits to schedule.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const POTENTIAL_RESCHEDULES_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.potentialReschedules),
    {
        defaultEnabled: true,
        hoverText: 'Visits that need to be either rescheduled or marked as missed.',
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No patient no-shows requiring attention.',
            ordering: { effectiveSortDate: Ordering.DESC },
        },
    },
);

const URGENT_VISITS_NEEDING_SCHEDULING_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.urgentVisitsNeedingScheduling),
    {
        defaultEnabled: true,
        hoverText: 'Visits that need to be scheduled within the next 7 days.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No visits with expiring fulfillment windows.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const PATIENTS_NEEDING_SCHEDULING_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.patientsNeedingScheduling),
    {
        defaultEnabled: true,
        hoverText: 'Patients with visits that need to be scheduled.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No patients to schedule.',
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
);

const OVERSCHEDULED_PATIENTS_CONTAINER: OrderDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.overscheduledPatients),
    {
        defaultEnabled: true,
        hoverText: 'Patients who have an overscheduled frequency.',
        tableConfig: {
            rowToLink: defaultEpisodeOrdersRowToLink,
            emptyTableText: 'No overscheduled patients.',
            ordering: { fulfillmentWindowStartDate: Ordering.ASC },
        },
    },
);

const UNDERSCHEDULED_PATIENTS_CONTAINER: OrderDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.underscheduledPatients),
    {
        defaultEnabled: true,
        hoverText: 'Frequency orders with fewer active visits than the order requires.',
        tableConfig: {
            rowToLink: defaultEpisodeOrdersRowToLink,
            emptyTableText: 'No frequency orders needing review.',
            ordering: { fulfillmentWindowStartDate: Ordering.ASC },
        },
    },
);

const INACTIVE_CLINICIAN_VISITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.inactiveClinicianVisits),
    {
        defaultEnabled: true,
        hoverText: 'Visits assigned to an inactive clinician.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultEpisodeVisitsRowToLink,
            emptyTableText: 'No visits assigned to inactive clinicians.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer =
    fromDefinition(
        definitionFor(
            PUBLISHED_QUEUES,
            PINNACLE_CONTAINER_KEYS.agencyDischargedPatientsWithPendingVisits,
        ),
        {
            defaultEnabled: false,
            hoverText:
                'Agency discharged patients with scheduled visits that need to be cancelled.',
            extraProps: CANCEL_VISITS,
            tableConfig: {
                highlightLinkedVisit: true,
                rowToLink: defaultEpisodeVisitsRowToLink,
                emptyTableText: 'No agency discharged patients with pending visits.',
                ordering: { effectiveSortDate: Ordering.ASC },
            },
        },
    );

const DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer =
    fromDefinition(
        definitionFor(
            PUBLISHED_QUEUES,
            PINNACLE_CONTAINER_KEYS.disciplineDischargePatientsWithPendingVisits,
        ),
        {
            defaultEnabled: false,
            hoverText:
                'Discipline discharged patients with scheduled visits that need to be canceled.',
            extraProps: CANCEL_VISITS,
            tableConfig: {
                highlightLinkedVisit: true,
                rowToLink: defaultEpisodeVisitsRowToLink,
                emptyTableText: 'No pending visits after discipline discharge.',
                ordering: { effectiveSortDate: Ordering.ASC },
            },
        },
    );

const DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer =
    fromDefinition(
        definitionFor(
            PUBLISHED_QUEUES,
            PINNACLE_CONTAINER_KEYS.discontinuedFrequenciesWithPendingVisits,
        ),
        {
            defaultEnabled: false,
            hoverText: 'Discontinued frequencies with scheduled visits that need to be cancelled.',
            extraProps: CANCEL_VISITS,
            tableConfig: {
                highlightLinkedVisit: true,
                rowToLink: defaultEpisodeVisitsRowToLink,
                emptyTableText: 'No visits tied to discontinued frequencies.',
                ordering: { effectiveSortDate: Ordering.ASC },
            },
        },
    );

const TRANSFERRED_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.transferredPatientsWithPendingVisits),
    {
        defaultEnabled: false,
        hoverText: 'Transferred patients with scheduled visits that need to be cancelled.',
        extraProps: CANCEL_VISITS,
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultEpisodeVisitsRowToLink,
            emptyTableText: 'No transfers requiring attention.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const PENDING_HHA_SUPERVISORY_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.pendingHhaSupervisory),
    {
        defaultEnabled: false,
        hoverText:
            'Patients needing an HHA Supervisory visit within 14 days of their last supervisory event.',
        tableConfig: {
            rowToLink: allEpisodesSchedulingLink,
            emptyTableText: 'No patients pending HHA supervisory visits.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const PENDING_LPN_SUPERVISORY_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.pendingLpnSupervisory),
    {
        defaultEnabled: false,
        hoverText:
            'Patients needing an LPN Supervisory visit within 30 days of their last supervisory event.',
        tableConfig: {
            rowToLink: allEpisodesSchedulingLink,
            emptyTableText: 'No patients pending LPN supervisory visits.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const PENDING_THERAPY_SUPERVISORY_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.pendingTherapySupervisory),
    {
        defaultEnabled: false,
        hoverText: 'Patients needing a PT or OT Supervisory visit within the next 7 days.',
        tableConfig: {
            rowToLink: allEpisodesSchedulingLink,
            emptyTableText: 'No patients pending therapy supervisory visits.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const PENDING_THERAPY_REEVALUATION_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.pendingTherapyReevaluation),
    {
        defaultEnabled: false,
        hoverText:
            'Patients needing a PT, OT, or ST re-eval within 30 days of their last reassessment.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients pending re-evaluation.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const UPCOMING_END_OF_EPISODE_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.upcomingEndOfEpisode),
    {
        defaultEnabled: false,
        hoverText:
            'Patients nearing the end of their episode with no recert, discharge, or recent ROC visit scheduled.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No episodes pending end of care.',
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
);

const VISITS_SCHEDULED_BEFORE_SOC_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.visitsScheduledBeforeSoc),
    {
        defaultEnabled: false,
        hoverText: "Visits that are scheduled or completed prior to the patient's SOC.",
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No Evals scheduled before SOCs.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const CONFLICTING_DISCHARGE_AND_RECERT_VISITS_CONTAINER: EpisodeOfCareDashboardContainer =
    fromDefinition(
        definitionFor(
            PUBLISHED_QUEUES,
            PINNACLE_CONTAINER_KEYS.conflictingDischargeAndRecertVisits,
        ),
        {
            defaultEnabled: false,
            hoverText: 'Patients with more than 1 OASIS discharge or recert visit on an episode.',
            tableConfig: {
                rowToLink: defaultEpisodeOfCareRowToLink,
                emptyTableText: 'No episodes with multiple OASIS Discharge or Recert visits.',
                ordering: { patient: { fullName: Ordering.ASC } },
            },
        },
    );

const UPCOMING_DISCHARGE_WITH_FUTURE_PENDING_VISITS_CONTAINER: VisitDashboardContainer =
    fromDefinition(
        definitionFor(
            PUBLISHED_QUEUES,
            PINNACLE_CONTAINER_KEYS.upcomingDischargeWithFuturePendingVisits,
        ),
        {
            defaultEnabled: false,
            hoverText:
                'Patients with an upcoming OASIS discharge who still have other visits scheduled after the discharge date.',
            tableConfig: {
                highlightLinkedVisit: true,
                rowToLink: defaultEpisodeVisitsRowToLink,
                emptyTableText: 'No patients with visits after discharge.',
                ordering: { effectiveSortDate: Ordering.ASC },
            },
        },
    );

const COMPLETED_VISITS_MISSING_AN_ORDER_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.completedVisitsMissingAnOrder),
    {
        defaultEnabled: false,
        hoverText: "Completed SOCs/ROCs/Evals that aren't yet associated to an order.",
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No completed SOC/ROC/Eval visits unlinked to orders.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const ACTIVE_PATIENTS_WITH_NO_VISITS_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.activePatientsWithNoVisits),
    {
        defaultEnabled: false,
        hoverText: "Patients who are active but don't have any future visits scheduled.",
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No active patients without pending visits.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const UNRESOLVED_EPISODES_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.unresolvedEpisodes),
    {
        defaultEnabled: true,
        hoverText:
            'Active or on-hold episodes whose end date has already passed \u2014 need a recert or discharge.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No unresolved episodes.',
            ordering: { endDate: Ordering.ASC },
        },
    },
);

const PATIENTS_MISSING_CMS_485_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.patientsMissingCms485),
    {
        defaultEnabled: false,
        hoverText: 'Patients missing a CMS-485 task same day as SOC.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients missing CMS-485.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const PATIENTS_MISSING_NOMNC_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.patientsMissingNomnc),
    {
        defaultEnabled: false,
        hoverText: 'Patients missing a NOMNC at least 48 hours before a discharge within 7 days.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients missing NOMNCs.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const PATIENTS_MISSING_SCIC_ORDER_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.patientsMissingScicOrder),
    {
        defaultEnabled: false,
        hoverText: 'Patients missing a SCIC Order task same day as Follow-Up.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients missing SCIC Orders.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const PATIENTS_MISSING_POST_HOSPITAL_ORDER_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.patientsMissingPostHospitalOrder),
    {
        defaultEnabled: false,
        hoverText: 'Patients missing a Post Hospital Order same day as ROC.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients missing Post Hospital Orders.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const VISITS_ASSIGNED_TO_CONTRACT_AGENCY_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.visitsAssignedToContractAgency),
    {
        defaultEnabled: false,
        hoverText: 'Visits sent to a contract agency with no individual contractor yet assigned.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No visits assigned to a contract agency.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const ALL_TRANSFERRED_PATIENTS_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.allTransferredPatients),
    {
        defaultEnabled: false,
        hoverText: 'All patients currently in transfer status.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No episodes pending in transfer.',
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
);

const MISSED_VISITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.missedVisits),
    {
        defaultEnabled: false,
        hoverText: 'All missed visits scheduled in the current Medicare week.',
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No missed visits this Medicare week.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const VISITS_WITHOUT_SHIFTS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.visitsWithoutActiveShifts),
    {
        defaultEnabled: true,
        hoverText: 'Visits without active shifts',
        extraProps: { showClinicianColumn: true },
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No visits without shifts.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const PINNACLE_VISITS_AWAITING_AUTHORIZATION_QUEUE: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PINNACLE_CONTAINER_KEYS.visitsAwaitingAuthorization),
    {
        defaultEnabled: true,
        hoverText:
            "Visits that can't be created in WellSky because there isn't an available authorization to cover them.",
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

export const PINNACLE_UAT_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    todaysVisitsFilter: TODAYS_VISITS_EXCLUDE_NON_VISIT_TASKS_FILTER,
    containers: [
        /* #1  */ PINNACLE_INCOMPLETE_CARE_TEAM_CONTAINER,
        /* #2  */ SOC_CONTAINER,
        /* #3  */ ROC_CONTAINER,
        /* #4  */ EVAL_CONTAINER,
        /* #6  */ POTENTIAL_RESCHEDULES_CONTAINER,
        /* #7  */ URGENT_VISITS_NEEDING_SCHEDULING_CONTAINER,
        /* #8  */ PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        /* #9  */ OVERSCHEDULED_PATIENTS_CONTAINER,
        /* #10 */ UNDERSCHEDULED_PATIENTS_CONTAINER,
        /* #11 */ INACTIVE_CLINICIAN_VISITS_CONTAINER,
        /* #12 */ AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #13 */ DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #14 */ DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS_CONTAINER,
        /* #15 */ TRANSFERRED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #16 */ PENDING_HHA_SUPERVISORY_CONTAINER,
        /* #17 */ PENDING_LPN_SUPERVISORY_CONTAINER,
        /* #18 */ PENDING_THERAPY_SUPERVISORY_CONTAINER,
        /* #19 */ PENDING_THERAPY_REEVALUATION_CONTAINER,
        /* #20 */ UPCOMING_END_OF_EPISODE_CONTAINER,
        /* #21 */ VISITS_SCHEDULED_BEFORE_SOC_CONTAINER,
        /* #22 */ CONFLICTING_DISCHARGE_AND_RECERT_VISITS_CONTAINER,
        /* #23 */ UPCOMING_DISCHARGE_WITH_FUTURE_PENDING_VISITS_CONTAINER,
        /* #24 */ COMPLETED_VISITS_MISSING_AN_ORDER_CONTAINER,
        /* #25 */ ACTIVE_PATIENTS_WITH_NO_VISITS_CONTAINER,
        /* #26 */ UNRESOLVED_EPISODES_CONTAINER,
        /* #29 */ PATIENTS_MISSING_CMS_485_CONTAINER,
        /* #30 */ PATIENTS_MISSING_NOMNC_CONTAINER,
        /* #31 */ PATIENTS_MISSING_SCIC_ORDER_CONTAINER,
        /* #32 */ PATIENTS_MISSING_POST_HOSPITAL_ORDER_CONTAINER,
        /* #33 */ VISITS_ASSIGNED_TO_CONTRACT_AGENCY_CONTAINER,
        /* #34 */ ALL_TRANSFERRED_PATIENTS_CONTAINER,
        /* #35 */ MISSED_VISITS_CONTAINER,
        /* #36 */ VISITS_WITHOUT_SHIFTS_CONTAINER,
        /* #37 */ PINNACLE_VISITS_AWAITING_AUTHORIZATION_QUEUE,
    ],
};

export const PINNACLE_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    todaysVisitsFilter: TODAYS_VISITS_EXCLUDE_NON_VISIT_TASKS_FILTER,
    containers: [
        /* #1  */ PINNACLE_INCOMPLETE_CARE_TEAM_CONTAINER,
        /* #2  */ SOC_CONTAINER,
        /* #3  */ ROC_CONTAINER,
        /* #4  */ EVAL_CONTAINER,
        // #6  held back for this environment: POTENTIAL_RESCHEDULES_CONTAINER
        /* #7  */ URGENT_VISITS_NEEDING_SCHEDULING_CONTAINER,
        /* #8  */ PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        /* #9  */ OVERSCHEDULED_PATIENTS_CONTAINER,
        /* #10 */ UNDERSCHEDULED_PATIENTS_CONTAINER,
        /* #11 */ INACTIVE_CLINICIAN_VISITS_CONTAINER,
        /* #12 */ AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #13 */ DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #14 */ DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS_CONTAINER,
        /* #15 */ TRANSFERRED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #16 */ PENDING_HHA_SUPERVISORY_CONTAINER,
        /* #17 */ PENDING_LPN_SUPERVISORY_CONTAINER,
        /* #18 */ PENDING_THERAPY_SUPERVISORY_CONTAINER,
        /* #19 */ PENDING_THERAPY_REEVALUATION_CONTAINER,
        /* #20 */ UPCOMING_END_OF_EPISODE_CONTAINER,
        /* #21 */ VISITS_SCHEDULED_BEFORE_SOC_CONTAINER,
        /* #22 */ CONFLICTING_DISCHARGE_AND_RECERT_VISITS_CONTAINER,
        /* #23 */ UPCOMING_DISCHARGE_WITH_FUTURE_PENDING_VISITS_CONTAINER,
        /* #24 */ COMPLETED_VISITS_MISSING_AN_ORDER_CONTAINER,
        /* #25 */ ACTIVE_PATIENTS_WITH_NO_VISITS_CONTAINER,
        // #26 held back for this environment: UNRESOLVED_EPISODES_CONTAINER
        /* #29 */ PATIENTS_MISSING_CMS_485_CONTAINER,
        /* #30 */ PATIENTS_MISSING_NOMNC_CONTAINER,
        /* #31 */ PATIENTS_MISSING_SCIC_ORDER_CONTAINER,
        /* #32 */ PATIENTS_MISSING_POST_HOSPITAL_ORDER_CONTAINER,
        /* #33 */ VISITS_ASSIGNED_TO_CONTRACT_AGENCY_CONTAINER,
        /* #34 */ ALL_TRANSFERRED_PATIENTS_CONTAINER,
        /* #35 */ MISSED_VISITS_CONTAINER,
        /* #36 */ VISITS_WITHOUT_SHIFTS_CONTAINER,
        /* #37 */ PINNACLE_VISITS_AWAITING_AUTHORIZATION_QUEUE,
    ],
};
